import { useLocation, useNavigate } from 'react-router';
import type { DialogRootProps } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Field,
  Fieldset,
  Flex,
  Float,
  HStack,
  Input,
  RadioGroup,
  Show,
  Skeleton,
  SkeletonCircle,
  Span,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Dialog, type ItemKey, LoadingButton, SideMenu, toaster } from '@halo/ui-react';
import { RippleEffect, UnreadBadge } from '@halo/ui-chat-react';
import info from 'virtual:build-info';
import {
  clearFileCache,
  getSessionsUnreadCount,
  getTotalFileCacheSize,
  useAppStore,
} from '@halo/chat-sdk-react';
import { walletTools } from '@halo/wallet-app';
import { useShallowEnhance, useStoreEnhance } from '@halo/zustand-enhance';
import Paths from '@/configs/router/paths';
import { navMenu } from '@/configs/router/menu';
import type { DebugLevel } from '@/scripts/debug';
import { clearAll, clearDB, debugLevel, setDebugLevel } from '@/scripts/debug';
import { useSettingsStore } from '@/store/settings/store';
import { DialogTrigger, GlobalSearchDialog } from '@/components/GlobalSearchDialog';
import { useActionsStore } from '@/store/actions';
import { SearchTabs } from '@/components/GlobalSearchDialog/helpers.tsx';
import type { RouteMenuItem } from '@/scripts/router/types';
import { exportOpfsToDirectory, importOpfs } from '@/scripts/debug/backup';
import { userApis } from '@/configs/apis';
import My from '../My';

const comingSoonModules = [Paths.CALLS, Paths.FEEDS];

const LoadingContent = memo(function LoadingContent() {
  return (
    <Stack gap="4" flex="1">
      <Flex gap="2">
        <SkeletonCircle size="12" />
        <Stack flex="1">
          <Skeleton height="5" width="80%" />
          <Skeleton height="5" width="80%" />
        </Stack>
      </Flex>
      <Box>
        <Skeleton height="10" />
      </Box>
      <Stack gap="2">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} height="10" />
        ))}
      </Stack>
    </Stack>
  );
});

interface NavItemProps {
  active: boolean;
  item: RouteMenuItem;
  isMiniNav: boolean;
  children?: ReactNode;
}

const NavItem = memo(function NavItem({ active, item, isMiniNav, children }: NavItemProps) {
  return (
    <RippleEffect key={item.key}>
      <SideMenu.Item
        data-path={item.key}
        userSelect="none"
        key={item.key}
        itemKey={item.key}
        active={active}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="2"
        p="2"
        borderRadius="lg"
        color="fg.muted"
        fontWeight="medium"
        fontSize={isMiniNav ? 'xs' : 'sm'}
        _hover={{ bg: 'bg.muted', color: 'fg', fontWeight: 'medium' }}
        _selected={{ bg: 'bg.muted', color: 'fg', fontWeight: 'medium' }}
        transition="all 0.2s"
      >
        <Box
          display="flex"
          alignItems="center"
          gap={isMiniNav ? '0.5' : '2'}
          flex="1"
          flexDirection={isMiniNav ? 'column' : 'row'}
          overflow="hidden"
          className="nav-shake"
        >
          <Box flexShrink="0" className="nav-icon-shake">
            {active ? item.activeIcon : item.icon}
          </Box>
          {!isMiniNav && <Box>{item.label}</Box>}
        </Box>
        {children}
      </SideMenu.Item>
    </RippleEffect>
  );
});

const NavMessagesItem = memo(function NavMessagesItem({ isMiniNav, ...props }: NavItemProps) {
  const sessions = useAppStore((state) => state.sessions);

  const { unread_count, muted_unread_count } = useMemo(() => {
    return getSessionsUnreadCount(sessions);
  }, [sessions]);

  const unreadBadge = useMemo(() => {
    return <UnreadBadge count={unread_count} mutedCount={muted_unread_count} />;
  }, [unread_count, muted_unread_count]);

  return (
    <NavItem isMiniNav={isMiniNav} {...props}>
      {isMiniNav ? (
        <Float placement="top-end" transform="translate(-50%, 50%)">
          {unreadBadge}
        </Float>
      ) : (
        unreadBadge
      )}
    </NavItem>
  );
});

const NavItems = memo(function NavItems({ isMiniNav }: { isMiniNav: boolean }) {
  const location = useLocation();

  return navMenu.map((item) => {
    const active = location.pathname.includes(item.key);
    if (item.key === Paths.MESSAGES) {
      return (
        <NavMessagesItem
          key={item.key}
          active={active}
          item={item}
          isMiniNav={isMiniNav}
        ></NavMessagesItem>
      );
    }

    return <NavItem key={item.key} active={active} item={item} isMiniNav={isMiniNav}></NavItem>;
  });
});

export const DebugForm = memo(function DebugForm({
  open,
  ...props
}: Omit<DialogRootProps, 'children'>) {
  const { editDelayedTime, setEditDelayedTime, luckyBoxDeadline, setLuckyBoxDeadline } =
    useSettingsStore();

  const [fetchTotalLoading, setFetchTotalLoading] = useState(false);
  const [totalFileCacheSize, setTotalFileCacheSize] = useState('0');

  const fetchTotalFileCacheSize = useCallback(async () => {
    setFetchTotalLoading(true);
    getTotalFileCacheSize()
      .then((size) => {
        setTotalFileCacheSize((size / 1024 / 1024).toFixed(2));
      })
      .finally(() => {
        setFetchTotalLoading(false);
      });
  }, []);

  useEffect(() => {
    if (open) {
      fetchTotalFileCacheSize();
    }
  }, [open, fetchTotalFileCacheSize]);

  return (
    debugLevel > 0 && (
      <Dialog.Root {...props} open={open}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Settings</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Fieldset.Root>
                <Fieldset.Content>
                  <Field.Root>
                    <Field.Label>Operates</Field.Label>
                    <HStack wrap="wrap">
                      <Button
                        size="2xs"
                        variant="outline"
                        onClick={() => {
                          clearDB();
                        }}
                      >
                        Clear DB
                      </Button>
                      <Button
                        size="2xs"
                        variant="outline"
                        onClick={() => {
                          clearAll();
                        }}
                      >
                        Clear All
                      </Button>
                      <LoadingButton
                        size="2xs"
                        variant="outline"
                        onClick={() => {
                          return clearFileCache().then(fetchTotalFileCacheSize);
                        }}
                      >
                        Clear File Cache
                        {fetchTotalLoading ? (
                          <Spinner size="xs" />
                        ) : (
                          <Text>({totalFileCacheSize} MB)</Text>
                        )}
                      </LoadingButton>
                      <LoadingButton
                        size="2xs"
                        variant="outline"
                        onClick={() => {
                          return exportOpfsToDirectory();
                        }}
                      >
                        Export DB
                      </LoadingButton>
                      <Button
                        size="2xs"
                        variant="outline"
                        onClick={() => {
                          importOpfs();
                        }}
                      >
                        Import DB
                      </Button>
                    </HStack>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Debug Level</Field.Label>
                    <RadioGroup.Root
                      defaultValue={debugLevel.toString()}
                      size="xs"
                      onValueChange={({ value }) => {
                        setDebugLevel(Number(value) as DebugLevel);
                      }}
                    >
                      <HStack gap="6">
                        {[
                          { value: '0', label: 'Disabled' },
                          { value: '1', label: 'Web Debug' },
                          { value: '2', label: 'Web Debug and Core Debug' },
                        ].map((item) => (
                          <RadioGroup.Item key={item.value} value={item.value}>
                            <RadioGroup.ItemHiddenInput />
                            <RadioGroup.ItemIndicator />
                            <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                          </RadioGroup.Item>
                        ))}
                      </HStack>
                    </RadioGroup.Root>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Edit Delayed Time (ms)</Field.Label>
                    <Input
                      type="number"
                      size="2xs"
                      value={editDelayedTime}
                      onChange={(e) => setEditDelayedTime(Number(e.target.value))}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Lucky Box Deadline (ms)</Field.Label>
                    <Input
                      type="number"
                      size="2xs"
                      value={luckyBoxDeadline}
                      onChange={(e) => setLuckyBoxDeadline(Number(e.target.value))}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Quick Send</Field.Label>
                    <QuickSend></QuickSend>
                  </Field.Root>
                </Fieldset.Content>
              </Fieldset.Root>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    )
  );
});

function QuickSend() {
  const { activatedSessionId, sessionStores } = useAppStore();
  const sessionStoreInstance = activatedSessionId ? sessionStores[activatedSessionId] : undefined;
  const sessionStore = useStoreEnhance(sessionStoreInstance);
  const draftMessage = sessionStore?.draftMessage;
  const [total, setTotal] = useState(10);
  const [loading, setLoading] = useState(false);

  const sendDraftMessage = useCallback(async () => {
    setLoading(true);
    for (let i = 0; i < total; i++) {
      await sessionStore?.sendDraftMessage(draftMessage);
    }
    setLoading(false);
  }, [draftMessage, sessionStore, total]);

  return (
    <Flex alignItems="center" gap="2">
      Total:
      <Input
        size="2xs"
        type="number"
        value={total}
        onChange={(e) => setTotal(Number(e.target.value))}
      />
      <Button
        size="2xs"
        loading={loading}
        disabled={!draftMessage?.payload}
        onClick={sendDraftMessage}
      >
        Send
      </Button>
    </Flex>
  );
}

export default memo(function Nav({
  hideNav,
  isMiniNav,
}: {
  hideNav: boolean;
  isMiniNav: boolean;
}) {
  const { t } = useTranslation();
  const { initTrigger, activatedSessionId } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const refTimerId = useRef<any>(0);
  const { globalSearchIsOpen, setGlobalSearchIsOpen, setUserOnboardingIsOpen } = useActionsStore(
    useShallowEnhance(['globalSearchIsOpen', 'setGlobalSearchIsOpen', 'setUserOnboardingIsOpen'])
  );
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  const versionSimple = useMemo(() => {
    return (
      <Text className="version_simple">
        <Span mr="2">{info.version.replace(/-\d+/, '')}</Span>
        <Span>{new Date(info.buildTime).toLocaleDateString()}</Span>
      </Text>
    );
  }, []);

  const versionFull = useMemo(() => {
    return (
      <Box className="version_full" display="none">
        <Text>{info.tag}</Text>
        <Text>{info.buildTime}</Text>
      </Box>
    );
  }, []);

  const handleSelectedKeyChange = useCallback(
    (key: ItemKey) => {
      if (comingSoonModules.includes(key as Paths)) {
        toaster.warning({
          title: t('tips.comingSoon'),
          description: t('tips.featureNotAvailableYet'),
        });
      } else {
        switch (key) {
          case Paths.MESSAGES:
            if (activatedSessionId) {
              navigate(Paths.MESSAGES_CHATS + '/' + activatedSessionId);
            } else {
              navigate(Paths.MESSAGES_CHATS);
            }
            break;
          case Paths.WALLET: {
            walletTools.toggleWalletApp();
            break;
          }
          default:
            navigate(key as string);
            break;
        }
      }
    },
    [activatedSessionId, navigate, t]
  );

  const handleSettingsDialogOpenChange = useCallback((details: any) => {
    setIsSettingsDialogOpen(details.open);
  }, []);

  /** link-msg-query */
  const { sessionStores, client } = useAppStore(useShallowEnhance(['sessionStores', 'client']));
  const searchState = useMemo(() => location?.state?.search, [location?.state?.search]);
  const extraState = useMemo(() => {
    const { dispose, isGroup } = location?.state ?? {};
    return {
      dispose,
      isGroup,
    };
  }, [location?.state]);

  const sessionStoreInstance = useMemo(
    () => sessionStores[searchState?.chat_id],
    [sessionStores, searchState?.chat_id]
  );
  const sessionStore = useStoreEnhance(sessionStoreInstance);

  useEffect(() => {
    if (!searchState || !client || !sessionStore?.activated) {
      return;
    }
    if (extraState?.dispose) {
      toaster.error({
        title: extraState?.isGroup ? t('tips.groupDisbanded') : t('tips.accountDeleted'),
      });
    }
    const { chat_id, cli_msg_id } = searchState;

    client.Api.messages
      .getMsgByCliMsgId(chat_id, cli_msg_id, (msg, isDelete) => {
        if (isDelete) {
          toaster.error({
            title: t('tips.messageDeleted'),
          });
        }
        if (msg && !isDelete) {
          sessionStore.scrollToMessage(msg, true);
        }
      })
      .finally(() => {
        navigate(location.pathname, {
          state: null,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionStore?.activated,
    sessionStore?.scrollToMessage,
    searchState,
    extraState,
    navigate,
    t,
  ]);
  /** link-msg-query end */

  const isNewUser = userApis.getIsNewUser();
  // user onBoarding
  useEffect(() => {
    setUserOnboardingIsOpen(isNewUser);
  }, [isNewUser, setUserOnboardingIsOpen]);

  return (
    <Box
      className="scrollHover"
      hidden={hideNav}
      as="nav"
      h="full"
      p={2.5}
      pt={6}
      gap={isMiniNav ? 8 : 2.5}
    >
      <SideMenu.Root
        selectedKey={location.pathname}
        onSelectedKeyChange={handleSelectedKeyChange}
      >
        <Show when={!!initTrigger} fallback={<LoadingContent />}>
          <SideMenu.Aside>
            <Flex direction="column" gap={isMiniNav ? 3 : 4}>
              <My isMiniNav={isMiniNav} />
              <GlobalSearchDialog isOpen={globalSearchIsOpen} setIsOpen={setGlobalSearchIsOpen}>
                <DialogTrigger
                  isMiniNav={isMiniNav}
                  handleClick={() => setGlobalSearchIsOpen(true, SearchTabs.TopResults)}
                />
              </GlobalSearchDialog>
            </Flex>
          </SideMenu.Aside>
          <SideMenu.Content
            className="SideMenuContent scrollHover"
            display="flex"
            flexDirection="column"
            mt="2"
            gap={isMiniNav ? '4' : '2'}
          >
            <NavItems isMiniNav={isMiniNav} />
          </SideMenu.Content>
        </Show>
        <SideMenu.Aside>
          <Box
            fontSize="xs"
            color="fg.muted"
            textAlign="center"
            cursor="pointer"
            css={{
              '&:hover .version_simple': {
                display: 'none',
              },
              '&:hover .version_full': {
                display: 'block',
              },
            }}
            onMouseDown={() => {
              refTimerId.current = setTimeout(() => {
                setDebugLevel((debugLevel + 1) as DebugLevel);
                window.location.reload();
              }, 2000);
            }}
            onMouseUp={() => {
              clearTimeout(refTimerId.current);
            }}
            onClick={() => setIsSettingsDialogOpen(true)}
          >
            {versionSimple}
            {versionFull}
          </Box>
          <DebugForm
            open={isSettingsDialogOpen}
            onOpenChange={handleSettingsDialogOpenChange}
          ></DebugForm>
        </SideMenu.Aside>
      </SideMenu.Root>
    </Box>
  );
});
